<template>
    <div class="playbook-card" :data-playbook-id="playbook.id">
        <div v-if="playbook.image" class="playbook-card__image">
            <img :src="playbook.image" :alt="playbook.title" />
        </div>
        <a
            v-if="playbook.is_sponsored"
            href="#"
            class="playbook-card__icon playbook-card__icon--sponsored"
            :aria-label="`Sponsored playbook: ${playbook.title}`"
            :title="'Sponsored - Free Access'"
            @click.prevent
        >
            <svg width="35" height="35" viewBox="0 0 35 35" fill="none">
                <path d="M17.5 2.917L21.875 11.667L31.667 13.125L24.583 20L26.25 29.792L17.5 25.208L8.75 29.792L10.417 20L3.333 13.125L13.125 11.667L17.5 2.917Z" fill="white"/>
            </svg>
        </a>
        <a
            v-else
            :href="playbook.purchase_url"
            class="playbook-card__icon"
            :aria-label="`Purchase ${playbook.title}`"
            :title="`Purchase for $${playbook.price}`"
        >
            <svg width="35" height="35" viewBox="0 0 35 35" fill="none">
                <path d="M29.167 8.75H5.833C4.913 8.75 4.167 9.496 4.167 10.417V24.583C4.167 25.504 4.913 26.25 5.833 26.25H29.167C30.087 26.25 30.833 25.504 30.833 24.583V10.417C30.833 9.496 30.087 8.75 29.167 8.75ZM17.5 21.875C15.199 21.875 13.333 20.009 13.333 17.708C13.333 15.407 15.199 13.542 17.5 13.542C19.801 13.542 21.667 15.407 21.667 17.708C21.667 20.009 19.801 21.875 17.5 21.875Z" fill="white"/>
            </svg>
        </a>
        <div class="playbook-card__content">
            <div class="playbook-card__meta">
                <span class="playbook-card__date">{{ playbook.date }}</span>
                <span class="playbook-card__type">{{ playbook.event_type_name }}</span>
            </div>
            <h3 class="playbook-card__title">{{ playbook.title }}</h3>
            <p class="playbook-card__location">{{ playbook.market_name }}</p>
            <a :href="playbook.purchase_url" class="playbook-card__cta">
                <span v-if="playbook.is_sponsored">View Summary</span>
                <span v-else>Purchase ${{ playbook.price }}</span>
                <svg class="playbook-card__cta-icon" width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.66865 5.09936L4.00675 9.76127L3.24072 8.99524L7.90263 4.33333H3.79367V3.25H9.75198V9.20833H8.66865V5.09936Z" fill="#EF8125"/>
                </svg>
            </a>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PlaybookCard',
    props: {
        playbook: {
            type: Object,
            required: true,
            validator: (playbook) => {
                return playbook && typeof playbook.title === 'string';
            },
        },
    },
};
</script>

<style scoped>
.playbook-card {
    position: relative;
    background: white;
    width: 245px;
    height: 300px;
    display: inline-block;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.playbook-card__image {
    position: relative;
    width: 175px;
    height: 70px;
    overflow: hidden;
}

.playbook-card__image img {
    width: 204px;
    height: 146px;
    object-fit: cover;
    margin-top: -28px;
}

.playbook-card__icon {
    position: absolute;
    top: 0;
    right: 0;
    width: 70px;
    height: 70px;
    background-color: #ef8125;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    mix-blend-mode: multiply;
    transition: background-color 0.3s ease, transform 0.2s ease;
    text-decoration: none;
    cursor: pointer;
}

.playbook-card__icon:hover {
    background-color: #d16d1e;
    transform: scale(1.05);
}

.playbook-card__icon--sponsored {
    background-color: #28a745;
}

.playbook-card__icon--sponsored:hover {
    background-color: #218838;
}

.playbook-card__content {
    padding: 13px;
    position: relative;
    height: calc(300px - 70px - 26px);
    display: flex;
    flex-direction: column;
}

.playbook-card__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 3px;
}

.playbook-card__date {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.6);
}

.playbook-card__type {
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 10px;
    color: #45adbf;
    text-transform: uppercase;
}

.playbook-card__title {
    font-family: 'Oswald', sans-serif;
    font-weight: 500;
    font-size: 16px;
    color: #ef8125;
    margin: 0 0 8px;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.playbook-card__location {
    font-family: 'Montserrat', sans-serif;
    font-weight: normal;
    font-size: 13px;
    color: #363636;
    line-height: 18px;
    margin-bottom: 0;
    flex: 1;
}

.playbook-card__cta {
    position: absolute;
    bottom: 13px;
    left: 13px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    cursor: pointer;
    text-decoration: none;
}

.playbook-card__cta:hover {
    color: #ef8125;
}

.playbook-card__cta:hover svg {
    transform: translateX(2px);
}

.playbook-card__cta-icon {
    transition: transform 0.2s ease;
}
</style>
